import React from 'react';
import '../../css/kiosk.css';

/**
 * On-screen number pad for the truck cabinet touchscreen.
 *
 * There is no built-in option to fall back on. Chromium's `--enable-virtual-keyboard` is ChromeOS
 * only, and the balena browser block's variables cover rotation, cursor, flags and window geometry
 * with nothing for input at all. A native X11 keyboard (onboard, matchbox) would have to share the
 * block's X socket, fight the kiosk window for stacking, and reach into Chromium over the AT-SPI
 * accessibility bus just to learn a field was focused — and it would die the moment the panel
 * re-enumerated, exactly as touch input already does. So we render the pad ourselves.
 *
 * Driven by focus rather than wired into each field. That matters: every input this serves lives in
 * a component that also runs on a driver's phone, where the real OS keyboard must keep working, so
 * those components are left untouched and this mounts once in Main behind the kiosk flag.
 *
 * Digits only. That covers everything the console asks a driver to enter — litres, tin counts, tank
 * ids. The two free-text fields offer canned phrases and are read-only here; typing prose on a
 * cabinet panel with wet gloves was never the answer, and a note can be added from the phone.
 */
class NumericKeypad extends React.Component {
    constructor(props) {
        super(props);
        this.state = { target: null, buffer: '' };
        this.keypadRef = React.createRef();
    }

    componentDidMount() {
        document.addEventListener('focusin', this.onFocusIn);
        document.addEventListener('focusout', this.onFocusOut);
    }

    componentWillUnmount() {
        document.removeEventListener('focusin', this.onFocusIn);
        document.removeEventListener('focusout', this.onFocusOut);
        clearTimeout(this.syncTimer);
    }

    /** Fields we drive: a number box the driver is actually allowed to change. */
    static servesElement(el) {
        return !!el
            && el.tagName === 'INPUT'
            && el.type === 'number'
            && !el.disabled
            && !el.readOnly;
    }

    onFocusIn = (e) => {
        // Any read-back still owed belongs to the field we are leaving, not the one arriving.
        clearTimeout(this.syncTimer);

        if (!NumericKeypad.servesElement(e.target)) {
            if (this.state.target) {
                this.setState({ target: null, buffer: '' });
            }
            return;
        }

        // The pad opens over the bottom of the screen, so make sure the field itself didn't just end
        // up underneath it.
        if (e.target.scrollIntoView) {
            e.target.scrollIntoView({ block: 'center' });
        }

        this.setState({ target: e.target, buffer: e.target.value || '' });

        // These fields select-all on focus (DriverPickupView's handleFocus), and they default to 0
        // rather than empty. Both mean the first digit should replace what's there, the same as it
        // would on a real keyboard with the text selected.
        this.replaceOnNextKey = true;
    };

    onFocusOut = (e) => {
        // Pressing a key can't blur the field — the pad cancels mousedown for exactly that reason —
        // but a stray touch event on some panels still can, so don't close if focus landed on us.
        const pad = this.keypadRef.current;
        if (pad && e.relatedTarget && pad.contains(e.relatedTarget)) {
            return;
        }
        this.setState({ target: null, buffer: '' });
    };

    /**
     * Writes in behind React's back.
     *
     * React tracks the last value it saw on the DOM node itself, and assigning `el.value` goes
     * through the setter React patched in — which updates that tracker, so React concludes nothing
     * changed and never fires onChange. Calling the prototype's original setter leaves the tracker
     * stale, which is what makes the input event we dispatch look like a real edit.
     */
    writeValue(el, value) {
        const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
        setter.call(el, value);
        el.dispatchEvent(new Event('input', { bubbles: true }));
    }

    /**
     * Re-reads what the field actually kept, once React has committed it.
     *
     * It has to be deferred. We dispatch the input event from inside our own click handler, so React
     * batches the field's update with ours and commits it only after we have returned — read the
     * value straight after writing it and you get the previous one back. A macrotask is late enough
     * in every environment, where flushSync is not (it does nothing inside a test's act wrapper).
     *
     * Worth the trouble because the components behind these inputs clamp what the driver types —
     * litres to two decimal places, tin counts through parseInt — and the readout should show what
     * landed rather than what we asked for.
     */
    syncFromField(pendingDot) {
        clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(() => {
            const el = this.state.target;
            if (!el) {
                return;
            }
            const landed = el.value || '';
            this.setState({ buffer: pendingDot ? landed + '.' : landed });
        }, 0);
    }

    press = (key) => {
        const { target } = this.state;
        if (!target) {
            return;
        }

        // The field, not our buffer, is the source of truth. The components behind these inputs
        // clamp what the driver types — litres to two decimal places, tin counts through parseInt
        // — and continuing from our own buffer would quietly build the next keypress on a value the
        // app had already rejected. The one exception is a buffer ending in a decimal point, which
        // a number input cannot hold and which we are therefore keeping on its behalf.
        const shown = target.value || '';
        const holdingDot = this.state.buffer.slice(-1) === '.'
            && shown === this.state.buffer.slice(0, -1);
        const current = this.replaceOnNextKey ? '' : (holdingDot ? this.state.buffer : shown);
        let next;

        if (key === 'back') {
            next = current.slice(0, -1);
        } else if (key === 'clear') {
            next = '';
        } else if (key === '.') {
            if (current.indexOf('.') !== -1) {
                return;
            }
            next = current === '' ? '0.' : `${current}.`;
        } else {
            next = current + key;
        }

        // "05" is a transcription error waiting to happen, and these fields start at 0.
        if (/^0\d/.test(next)) {
            next = next.replace(/^0+/, '');
        }

        this.replaceOnNextKey = false;

        // A number input discards anything that isn't a valid number, and a bare trailing dot isn't
        // one — writing "0." would blank the field mid-entry. Hold the dot back and let the field
        // show "0" until the driver taps the digit that completes it.
        const trailingDot = next.slice(-1) === '.';
        this.writeValue(target, trailingDot ? next.slice(0, -1) : next);

        // Optimistic, so the readout responds to the tap immediately; syncFromField corrects it to
        // whatever the field kept a moment later.
        this.setState({ buffer: next });
        this.syncFromField(trailingDot);
    };

    close = () => {
        clearTimeout(this.syncTimer);
        const { target } = this.state;
        if (target) {
            target.blur();
        }
        this.setState({ target: null, buffer: '' });
    };

    render() {
        if (!this.state.target) {
            return null;
        }

        const key = (label, value, className) => (
            <button type="button"
                    className={`kiosk-key ${className || ''}`}
                    onClick={() => this.press(value)}>{label}</button>
        );

        // Cancelling mousedown is what keeps focus on the field: without it the first tap blurs the
        // input, the pad unmounts, and nothing is ever entered. It has to be mousedown and not
        // touchstart — cancelling touchstart would also suppress the emulated click, leaving every
        // key dead on the one device this is for. Cancelling mousedown stops the focus change and
        // leaves click alone.
        return (
            <div className="kiosk-keypad"
                 ref={this.keypadRef}
                 onMouseDown={(e) => e.preventDefault()}>
                <div className="kiosk-keypad-readout">{this.state.buffer || '0'}</div>
                <div className="kiosk-keypad-grid">
                    {key('7', '7')}
                    {key('8', '8')}
                    {key('9', '9')}
                    {key('4', '4')}
                    {key('5', '5')}
                    {key('6', '6')}
                    {key('1', '1')}
                    {key('2', '2')}
                    {key('3', '3')}
                    {key('.', '.')}
                    {key('0', '0')}
                    <button type="button"
                            className="kiosk-key kiosk-key-back"
                            aria-label="Backspace"
                            onClick={() => this.press('back')}><i className="fa fa-long-arrow-left" /></button>
                </div>
                <div className="kiosk-keypad-actions">
                    <button type="button" className="kiosk-key kiosk-key-clear"
                            onClick={() => this.press('clear')}>Clear</button>
                    <button type="button" className="kiosk-key kiosk-key-done"
                            onClick={this.close}>Done</button>
                </div>
            </div>
        );
    }
}

export default NumericKeypad;
